'use client';

import { useChat } from '@/contexts/ChatProvider';
import { useId } from 'react';
import './loading.css';
import { MessageRow } from './MessageRow';
import { GettingStarted } from './GettingStarted';
import { ChatMessage } from './ChatMessage';
import { SupportHelp } from './SupportHelp.client';

/**
 * Main chat messages container that handles the display of all messages
 * and related UI elements like the loading indicator and support help.
 */
export function Messages() {
  const id = useId();
  const { messages, status, error } = useChat();

  // Auto-scroll to bottom when new message is being generated
  if (status === 'in_progress') {
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    });
  }

  // Show getting started UI if no messages
  if (!messages.length) {
    return <GettingStarted />;
  }

  // Log any errors to console
  if (error) {
    console.error(error.message);
  }

  // Filter out system messages, which aren't shown to users
  const displayMessages = messages.filter(m => m.role !== 'system');
  const lastMessageType = messages[messages.length - 1].role;

  return (
    <div 
      className="flex flex-col gap-4 p-4" 
      role="log" 
      aria-live="polite" 
      aria-relevant="additions" 
      aria-label="Acquia Copilot"
    >
      {/* Render messages */}
      {displayMessages.map((message, index) => (
        <ChatMessage 
          key={message.id || index}
          message={message} 
          isLast={index === (displayMessages.length - 1)} 
        />
      ))}
      
      {/* Loading indicator */}
      {status === 'in_progress' && (
        <MessageRow orientation="left">
          <div className="max-w-lg mr-auto">
            <div id={id} className="mr-auto loader w-6" />
            {lastMessageType === 'user' && (
              <>
                <span className="sr-only">Please wait...</span>
                <p className="text-sm italic text-gray-600">
                  This may take a few moments to consult our knowledge bases and construct a response.
                </p>
              </>
            )}
          </div>
        </MessageRow>
      )}
      
      {/* Support help (only shown after first message and when not loading) */}
      {displayMessages.length >= 1 && status !== 'in_progress' && (
        <MessageRow orientation="left">
          <SupportHelp isChat={true} />
        </MessageRow>
      )}
      
      {/* Error message */}
      {error && (
        <div className="bg-orange-300 border-orange-600 border-2 p-4 rounded-lg">
          An error occurred. Please try again or contact support.
        </div>
      )}
    </div>
  );
}